import { Injectable, isDevMode } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FullChild } from '../../../../interfaces/FullChild';
import { environment } from '../../../../../environments/environment';

/**
 * How reliable the computed date of death is.
 *  - Exact                        : from an actual Om Shanti record (omShantiDetails / ALOmShantiRequest).
 *  - Estimated (discharge date)   : from the discharge date of an "OM SHANTI" discharge.
 *  - Estimated (request updated)  : last-resort fallback to the Om Shanti request's updatedAt.
 *  - Unknown                      : flagged OM SHANTI but no date anywhere.
 */
export type DateBasis =
  | 'Exact'
  | 'Estimated (discharge date)'
  | 'Estimated (request updated)'
  | 'Unknown';

export interface OmShantiRow {
  childCode: string;
  childName: string;
  gender: string;
  centreCode: string;
  centreName: string;
  status: string;
  dateOfOmShanti: Date | null;
  dateBasis: DateBasis;
  modeOmShanti: string;
  requestStatus: string;
  remark: string;
  dateOfAdmission: Date | null;
}

/**
 * Single source of truth for "who is an OM SHANTI child" and "when did it happen".
 * Shared by the OM SHANTI detail report and the OM SHANTI count report so the two
 * can never drift apart.
 */
@Injectable({ providedIn: 'root' })
export class OmShantiService {
  /** ALOmShantiRequest is a fixed top-level collection — not part of the testMode/productionMode maps. */
  private readonly OM_SHANTI_REQUESTS = 'ALOmShantiRequest';
  private readonly env = isDevMode() ? environment.testMode : environment.productionMode;

  constructor(private readonly mFirestore: AngularFirestore) {}

  /**
   * Loads every OM SHANTI child once and maps each to an {@link OmShantiRow}.
   * A child can be flagged three different ways depending on the flow used:
   *   status === 'OM SHANTI'  (discharge flow / approved Om Shanti request)
   *   expired === true        (legacy discharge flow)
   *   isOmShanti === true     (approved Om Shanti request only)
   * plus every ALOmShantiRequest doc (covers pending / not-yet-synced children).
   */
  fetchRows(): Observable<OmShantiRow[]> {
    const childCol = this.env.child;

    const byStatus$ = this.mFirestore
      .collection<FullChild>(childCol, ref => ref.where('status', '==', 'OM SHANTI'))
      .get();
    const byExpired$ = this.mFirestore
      .collection<FullChild>(childCol, ref => ref.where('expired', '==', true))
      .get();
    const byFlag$ = this.mFirestore
      .collection<FullChild>(childCol, ref => ref.where('isOmShanti', '==', true))
      .get();
    const requests$ = this.mFirestore.collection(this.OM_SHANTI_REQUESTS).get();

    return forkJoin([byStatus$, byExpired$, byFlag$, requests$]).pipe(
      map(([statusSnap, expiredSnap, flagSnap, reqSnap]) => {
        const childByCode = new Map<string, any>();
        const collect = (snap: any) => {
          snap.forEach((doc: any) => {
            const data = doc.data();
            const code = data.childCode || doc.id;
            if (!childByCode.has(code)) childByCode.set(code, { id: doc.id, ...data });
          });
        };
        collect(statusSnap);
        collect(expiredSnap);
        collect(flagSnap);

        const reqByCode = new Map<string, any>();
        reqSnap.forEach((doc: any) => {
          const data = doc.data();
          reqByCode.set(data.childCode || doc.id, { id: doc.id, ...data });
        });
        reqByCode.forEach((_req, code) => {
          if (!childByCode.has(code)) childByCode.set(code, null);
        });

        const rows: OmShantiRow[] = [];
        childByCode.forEach((child, code) => {
          rows.push(this.buildRow(code, child, reqByCode.get(code) || null));
        });
        rows.sort((a, b) => (b.dateOfOmShanti?.getTime() || 0) - (a.dateOfOmShanti?.getTime() || 0));
        return rows;
      })
    );
  }

  private buildRow(code: string, child: any, req: any): OmShantiRow {
    const omDetails = child?.omShantiDetails || null;

    let date: Date | null = null;
    let basis: DateBasis = 'Unknown';

    // 1. Exact — an actual Om Shanti record
    const exact = this.toDate(omDetails?.dateOfOmShanti) || this.toDate(req?.dateOfOmShanti);
    if (exact) {
      date = exact;
      basis = 'Exact';
    }

    // 2. Estimated — discharge date of an "OM SHANTI" discharge
    if (!date) {
      let admissionDischarge: Date | null = null;
      if (Array.isArray(child?.admissionData)) {
        const omDischarges = child.admissionData
          .filter((a: any) => a && (a.reason || '').toUpperCase() === 'OM SHANTI')
          .map((a: any) => this.toDate(a.dateOfDischarge))
          .filter((d: Date | null): d is Date => !!d)
          .sort((a: Date, b: Date) => b.getTime() - a.getTime());
        admissionDischarge = omDischarges[0] || null;
      }
      const reasonIsOmShanti =
        (child?.reason || '').toUpperCase() === 'OM SHANTI' ||
        (child?.status || '').toUpperCase() === 'OM SHANTI' ||
        child?.expired === true;
      const dischargeDate = reasonIsOmShanti
        ? this.toDate(child?.dischargeDate) || this.toDate(child?.dateOfDischarge)
        : null;
      const estimated = admissionDischarge || dischargeDate;
      if (estimated) {
        date = estimated;
        basis = 'Estimated (discharge date)';
      }
    }

    // 3. Last resort — the Om Shanti request's updatedAt
    if (!date) {
      const updated = this.toDate(req?.updatedAt);
      if (updated) {
        date = updated;
        basis = 'Estimated (request updated)';
      }
    }

    const name =
      (child ? `${child.firstName || ''} ${child.lastName || ''}`.trim() : '') ||
      req?.childName ||
      omDetails?.childName ||
      '';

    let status = child?.status || '';
    if (!status && child?.expired) status = 'expired';
    if (!child) status = 'Request only';

    return {
      childCode: code,
      childName: name,
      gender: child?.gender || '',
      centreCode: child?.centreCode || child?.baseCentreCode || '',
      centreName: child?.centreName || child?.baseCentreName || '',
      status,
      dateOfOmShanti: date,
      dateBasis: basis,
      modeOmShanti: omDetails?.modeOmShanti || req?.modeOmShanti || '',
      requestStatus: req?.requestStatus || '',
      remark: omDetails?.remark || req?.remark || child?.remark || '',
      dateOfAdmission: this.toDate(child?.dateOfAdmission)
    };
  }

  toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value.toDate === 'function') {
      const d = value.toDate();
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
}
